import { useEffect, useState } from "react";

const HIDE_FIELDS = new Set([
  "password", "otp", "email_otp", "createdAt", "updatedAt", "user_id", "device_type", "device_udid", "reason", "status", "is_verified", 
  "referral_code", "first_transaction_at", "first_transaction_done", "referrer_user_id",
]);
const READONLY_FIELDS = new Set(["email", "mobile_no"]);

const GENDER_OPTIONS = [
  { label: "Select Gender", value: "" },
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Other", value: "other" },
];

function formatDateForInput(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10); 
}

function normalizeGender(val) {
  if (!val) return "";
  const g = String(val).trim().toLowerCase();

  if (g === "m" || g === "male") return "male";
  if (g === "f" || g === "female") return "female";
  if (g === "o" || g === "other") return "other";

  if (g === "1") return "male";
  if (g === "2") return "female";
  if (g === "3") return "other";

  return "";
}

export default function UserEditModal({ open, onClose, loading, saving, user, onConfirm }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (user) {
      const copy = { ...user };

      for (const key of Object.keys(copy)) {
        if (HIDE_FIELDS.has(key)) delete copy[key];
      }

      copy.dob = formatDateForInput(copy.dob);

      copy.gender = normalizeGender(copy.gender);

      setForm(copy);
    }
  }, [user]);

  if (!open) return null;

  const handleChange = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = { ...form };

    for (const k of READONLY_FIELDS) {
      delete payload[k];
    }

    if (payload.dob === "" || payload.dob === "Invalid date") { payload.dob = null; }

    if (payload.gender === "") { payload.gender = null; }

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) delete payload[key];
    });

    onConfirm(payload);
  };

  const renderInput = (key) => {
    const readonly = READONLY_FIELDS.has(key);

    if (key === "dob") {
      return (
        <input
          type="date"
          value={form[key] ?? ""}
          readOnly={readonly}
          onChange={(e) => handleChange(key, e.target.value)}
          className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${
            readonly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
        />
      );
    }

    if (key === "gender") {
      return (
        <select
          value={form[key] ?? ""}
          disabled={readonly}
          onChange={(e) => handleChange(key, e.target.value)}
          className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${
            readonly ? "bg-gray-100 cursor-not-allowed" : ""
          }`}
        >
          {GENDER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type="text"
        value={form[key] ?? ""}
        readOnly={readonly}
        onChange={(e) => handleChange(key, e.target.value)}
        className={`mt-1 w-full border rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-black ${
          readonly ? "bg-gray-100 cursor-not-allowed" : ""
        }`}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-bold">Edit User</h2>
          <button onClick={onClose} className="text-sm font-semibold px-3 py-1 rounded-lg border">
            Close
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-gray-600">Loading...</div>
        ) : !user ? (
          <div className="p-6 text-sm text-gray-600">No data</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.keys(form).map((key) => (
                <div key={key}>
                  <label className="text-xs font-semibold capitalize">
                    {key.replaceAll("_", " ")}
                    {READONLY_FIELDS.has(key) ? " (readonly)" : ""}
                  </label>
                  {renderInput(key)}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <button type="button" onClick={onClose} className="rounded-xl border px-4 py-2 text-sm font-semibold">
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving ? "Updating..." : "Update"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
